/**
 * Tenant mode detection for the frontend (AC-PROD-1, AC-PROD-4).
 *
 * Classifies the current hostname against VITE_TENANT_BASE_DOMAIN
 * (same logic as server/lib/tenancy/hostClassifier.ts but client-side).
 */

import { useMemo } from 'react';
import { appConfig } from '@/config/app';

export type ClientHostMode = 'apex' | 'www' | 'tenant' | 'legacy';

export interface TenantModeInfo {
  mode: ClientHostMode;
  /** Tenant slug — only when mode === 'tenant' */
  tenantSlug?: string;
  /** True when running in tenant or apex mode */
  isTenantHost: boolean;
  /** True when org switcher should be locked (tenant mode) */
  lockOrgSwitcher: boolean;
}

export function useTenantMode(): TenantModeInfo {
  return useMemo(() => {
    const base = appConfig.tenantBaseDomain?.toLowerCase();
    if (!base || typeof window === 'undefined') {
      return { mode: 'legacy', isTenantHost: false, lockOrgSwitcher: false };
    }

    const hostname = window.location.hostname.toLowerCase();

    if (hostname === base) {
      return { mode: 'apex', isTenantHost: true, lockOrgSwitcher: false };
    }

    if (hostname === `www.${base}`) {
      return { mode: 'www', isTenantHost: true, lockOrgSwitcher: false };
    }

    if (hostname.endsWith(`.${base}`)) {
      const prefix = hostname.slice(0, hostname.length - (base.length + 1));
      if (prefix && !prefix.includes('.')) {
        return {
          mode: 'tenant',
          tenantSlug: prefix,
          isTenantHost: true,
          lockOrgSwitcher: true,
        };
      }
    }

    return { mode: 'legacy', isTenantHost: false, lockOrgSwitcher: false };
  }, []);
}
